/**
 * Toasts Store
 * Alpine.js reactive store for toast notifications
 */

import Alpine from 'alpinejs'
import type { ToastStore, ToastType } from '../types'

// ============================================================================
// Types
// ============================================================================

interface Toast {
  id: number
  message: string
  type: ToastType
  duration: number
}

// ============================================================================
// Store Definition
// ============================================================================

export function createToastsStore(): ToastStore & {
  items: Toast[]
  nextId: number
  remove(id: number): void
} {
  return {
    items: [],
    nextId: 0,

    /**
     * Show a toast notification
     */
    show(message: string, type: ToastType = 'info', duration = 4000): void {
      const id = this.nextId++

      this.items.push({
        id,
        message,
        type,
        duration,
      })

      // Auto-remove after duration
      if (duration > 0) {
        setTimeout(() => {
          this.remove(id)
        }, duration)
      }
    },

    /**
     * Show success toast
     */
    success(message: string, duration = 4000): void {
      this.show(message, 'success', duration)
    },

    /**
     * Show error toast
     */
    error(message: string, duration = 6000): void {
      this.show(message, 'error', duration)
    },

    /**
     * Show info toast
     */
    info(message: string, duration = 4000): void {
      this.show(message, 'info', duration)
    },

    /**
     * Show warning toast
     */
    warning(message: string, duration = 5000): void {
      this.show(message, 'warning', duration)
    },

    /**
     * Remove a toast by ID
     */
    remove(id: number): void {
      const index = this.items.findIndex((t) => t.id === id)
      if (index !== -1) {
        this.items.splice(index, 1)
      }
    },

    /**
     * Clear all toasts
     */
    clear(): void {
      this.items = []
    },
  }
}

// ============================================================================
// Register Store
// ============================================================================

export function registerToastsStore(): void {
  Alpine.store('toasts', createToastsStore())
}

// ============================================================================
// Type Declaration for Alpine
// ============================================================================

declare module 'alpinejs' {
  interface Stores {
    toasts: ReturnType<typeof createToastsStore>
  }
}

// ============================================================================
// Toast Component Data
// ============================================================================

/**
 * Alpine.js component for individual toast items
 */
export function toastItem() {
  return {
    show: false,

    init(): void {
      // Animate in
      setTimeout(() => {
        this.show = true
      }, 10)
    },

    close(): void {
      this.show = false
    },

    /**
     * Get sprite icon ID for toast type.
     * Per decision D4 the warning variant reuses the error glyph.
     */
    getIcon(type: ToastType): string {
      const icons: Record<ToastType, string> = {
        success: 'i-check-circle',
        error: 'i-error',
        warning: 'i-error',
        info: 'i-info',
      }
      return icons[type]
    },

    /**
     * Get container class string for toast type.
     * Uses the new `.toast` shell + variant alias (left-border accent).
     */
    getColorClasses(type: ToastType): string {
      const colors: Record<ToastType, string> = {
        success: 'toast toast-success',
        error: 'toast toast-error',
        warning: 'toast toast-warning',
        info: 'toast toast-info',
      }
      return colors[type]
    },
  }
}
